import React, { useState, useEffect, useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Mail } from "lucide-react";
import { Button } from "./Components/ui/button.jsx";
import ButtonLoader from "./Components/Loader";
import DrawerContainer from "./Components/DrawerContainer/DrawerContainer";

// Component imports
import SendMoney from "./Components/SendMoney/SendMoney";
import TransferDetails from "./Container/AccountOverview/TransferDetails";
import PaymentSummary from "./Container/AccountOverview/PaymentSummary";
import PaymentConfirmation from "./Container/AccountOverview/PaymentSuccesfull";
import AddBeneficiary from "./Container/AccountOverview/AddBenificary";
import AddBeneficiary2 from "./Container/AccountOverview/AddBenificary2";
import WalletTransfer from "./Container/AccountOverview/WalletTransfer";
import WalletSummary from "./Container/AccountOverview/WalletConvert/ConvertSummart";
import WalletConfirmation from "./Container/AccountOverview/WalletConfirmation";
import AddFundsDrawer from "./Container/AccountOverview/AddFunds";
import BenificaryList from "./Container/AccountOverview/BenificaryList";
import ChangeMethod from "./Container/AccountOverview/ChangeMethod";
import ManageCard from "./Container/MultiCurrencyBank/ManageCard";
import OtpConfirmation from "./Container/AccountOverview/OtpConfiramtion";
import BeneficiaryAdded from "./Container/AccountOverview/BeneficiaryAdded";

// Utilities
import { validateField, validateFieldSecond } from "./validation";
import {
  buildSupportEmailUrl,
  filterAddressFields,
  filterFields,
} from "./Utils/helperFunction";
import {
  PostBeneficiary,
  PostWalletTransfer,
  SendTransactionOtp,
  TransferFund,
} from "./api";
import { useOverview } from "./hooks";
import { API_CHANNEL } from "./Container/Auth/TwoFactor/constants";
import {
  INITIAL_ERRORS,
  INITIAL_FORM_DATA,
  OTP_LENGTH,
  OTP_LOCKOUT_CLOSE_MS,
} from "./Utils/Contant.js";

const CustomSheet = ({ isOpen, setIsOpen }) => {
  const queryClient = useQueryClient();
  const {
    loading,
    stateVariable,
    setBeniCreationError,
    setStateVariable,
    currentFlow,
    purposeOfTransfer,
    setCurrentFlow,
    amount,
    toAmount,
    selectedCurrency,
    data,
    setToAmount,
    setAmount,
    selectedToCurrency,
    selectedMethod,
    setCharges,
    pin,
    setPin,
    challengeId,
    setChallengeId,
    setPinError,
    setPurposeError,
    isLoading,
    toAmountError,
    setToAmountError,
    setSelectedMethod,
    setRemarks,
    countryList,
    useCountryList,
    setData,
    setFormattedFields,
    remarks,
    setPurposeOfTransfer,
    setBenificaryData,
    beneficiarySelected,
    setBeneficiarySelected,
    setFromAmountError,
    setRemarkError,
    setInvestmentAccount,
    fromAmountConversionError,
    setFromAmountConversionError,
    setisLoadingTransfer,
    investmentAccount,
    isLoadingTransfer,
    selectedMethodToTransfer,
    usePollingBeneficiaryCheck,
    fromAmountError,
    setIsHedgeFundAccount,
  } = useOverview();

  const [beneficiaryId, setBeneficiaryId] = useState(null);
  const [addedBeneficiary, setAddedBeneficiary] = useState(null);
  const [changeMode, setChangeMode] = useState(false);
  const [dynamicValidation, setDynamicValidation] = useState({});
  const [dynamicAddressValidation, setDynamicAddressValidation] = useState({});
  const [walletTransferDetails, setWalletTransferDetails] = useState({});
  const [conversionError, setConversionError] = useState("");
  const [otpLockedOut, setOtpLockedOut] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [errors, setErrors] = useState(INITIAL_ERRORS);

  // Card management state
  const [isCardFrozen, setIsCardFrozen] = useState(false);
  const [hasLimitErrors, setHasLimitErrors] = useState(false);
  const [isSavingLimits, setIsSavingLimits] = useState(false);

  const handleStatusChange = () => {
    if (currentFlow === "ChangeMethod") {
      setisLoadingTransfer(false);
      setStateVariable(2);
      setCurrentFlow("SendMoney");
      return;
    }
    setFormData({});
    setFormattedFields([]);
    setisLoadingTransfer(false);
    setData([]);
    setStateVariable(1);
    setCurrentFlow("SendMoney");
  };

  // Api to call County List
  useCountryList();
  const handleManualReview = (beneficiary) => {
    setAddedBeneficiary(beneficiary);
    setFormData({});
    setFormattedFields([]);
    setisLoadingTransfer(false);
    setData([]);
    setBeneficiaryId(null);
    setStateVariable(1);
    setCurrentFlow("beneficiaryAdded");
  };

  usePollingBeneficiaryCheck(
    beneficiaryId,
    handleStatusChange,
    handleManualReview
  );

  // Reset state when component unmounts or when drawer closes
  useEffect(() => {
    return () => {
      resetState();
    };
  }, []);

  // Reset state when isOpen changes from true to false
  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen]);

  const getFirstValidRoutingType = (formattedFields, fieldName) => {
    const field = formattedFields?.find((f) => f.fieldName === fieldName);
    if (field && field.validation) {
      // Check if validation contains pipe-separated options (like "bsb|phone_number|email_address")
      if (field.validation.includes("|")) {
        const options = field.validation.split("|");
        return options[0]; // Return the first valid option
      } else {
        // Handle regex patterns like "^institution_number$" or "^transit_number$"
        const match = field.validation.match(/\^([^$]+)\$/);
        if (match) {
          return match[1]; // Extract the value between ^ and $
        }
        // If no regex pattern, return the validation as is (cleaned)
        return field.validation.replace(/[^a-zA-Z0-9_]/g, "");
      }
    }
    return "";
  };

  const resetState = useCallback(() => {
    setFormData(INITIAL_FORM_DATA);
    setErrors(INITIAL_ERRORS);
    setChangeMode(false);
    setFromAmountError(false);
    setToAmountError(false);
    setStateVariable(1);
    setCurrentFlow("SendMoney");
    setInvestmentAccount("");
    setToAmount(null);
    setAmount(null);
    setData([]);
    setPin(Array(OTP_LENGTH).fill(""));
    setChallengeId("");
    setRemarks("");
    setPurposeOfTransfer("");
    setSelectedMethod(null);
    setCharges({
      totalFees: "-",
      rate: null,
    });
    setFormattedFields([]);
    setBeneficiarySelected({});
    setFromAmountConversionError("");
    setBeneficiaryId(null);
    setDynamicValidation({});
    setDynamicAddressValidation({});
    setWalletTransferDetails({});
    setConversionError("");
    setRemarkError("");
    setPinError("");
    setHasLimitErrors(false);

    // Invalidate queries to refresh data
    queryClient.invalidateQueries({
      queryKey: ["transactionDetails"],
    });
    queryClient.invalidateQueries({
      queryKey: ["uniqueWalletDetails"],
    });
  }, [
    queryClient,
    setAmount,
    setBeneficiarySelected,
    setCharges,
    setCurrentFlow,
    setData,
    setFormattedFields,
    setFromAmountError,
    setFromAmountConversionError,
    setInvestmentAccount,
    setChallengeId,
    setPin,
    setPinError,
    setRemarkError,
    setPurposeOfTransfer,
    setRemarks,
    setSelectedMethod,
    setStateVariable,
    setToAmount,
    setToAmountError,
    setHasLimitErrors,
  ]);

  const getTitle = () => {
    if (currentFlow === "SendMoney")
      switch (stateVariable) {
        case 1:
          return "Send Money";
        case 2:
          return "Send Money To";
        case 3:
          return "Payment Summary";
        case 4:
          return "Confirm Transfer";
        case 5:
          return "Payment Confirmation";
        default:
          return "Send Money";
      }
    if (currentFlow === "addNewPayee")
      switch (stateVariable) {
        case 1:
          return "Add New Payee";

        default:
          return "Add New Payee";
      }
    if (currentFlow === "beneficiaryAdded") return "Add new Payee";
    if (currentFlow === "AddFunds") return "Add Money";
    if (currentFlow === "beniList") return "Your Payees";
    if (currentFlow === "ChangeMethod") return "Create new Beneficiary";
    if (currentFlow === "ManageCard") return "Manage Card";
    if (currentFlow === "WalletTransfer")
      switch (stateVariable) {
        case 3:
          return "Confirm Conversion";
        default:
          return "Conversion";
      }
  };

  const validateForm3 = (formField, value) => {
    const fieldDef = filterAddressFields(data).find(
      (f) => f.fieldName === formField
    );
    const error = validateFieldSecond(fieldDef, value);

    return error;
  };

  const handleInputChange = (field, value) => {
    setBeniCreationError("");
    setErrors((prevErrors) => ({
      ...prevErrors,
      [field]: "",
    }));

    function getCountryDetailsByName(countryName) {
      const country = countryList?.find(
        (c) => c.countryName.toLowerCase() === countryName.toLowerCase()
      );
      if (country) {
        return {
          country_name: country.countryName,
          country_code: country.countryCode2,
          account_currency: country.currency,
        };
      } else {
        return null;
      }
    }

    const capitalizeFirstLetter = (str) => {
      if (typeof str !== "string" || !str) return str;
      return str.charAt(0).toUpperCase() + str.slice(1);
    };

    const countryDetails = getCountryDetailsByName(value);

    if (field === "country_code" && countryDetails) {
      setFormData((prevData) => ({
        ...prevData,
        country_name: countryDetails.country_name,
        country_code: countryDetails.country_code,
        account_currency: countryDetails.account_currency,
        bank_country_code: countryDetails.country_code,
      }));
    } else if (field === "swift_code") {
      setFormData((prevData) => ({
        ...prevData,
        [field]: value?.toUpperCase(),
      }));
    } else if (field === "account_routing_value1") {
      setFormData((prevData) => ({
        ...prevData,
        [field]: value?.toUpperCase(),
        // Dynamically set routing type from current form fields
        ["account_routing_type1"]: getFirstValidRoutingType(
          dynamicValidation,
          "account_routing_type1"
        ),
      }));
    } else if (field === "account_routing_value2") {
      setFormData((prevData) => ({
        ...prevData,
        [field]: value?.toUpperCase(),
        // Dynamically set routing type from current form fields
        ["account_routing_type2"]: getFirstValidRoutingType(
          dynamicValidation,
          "account_routing_type2"
        ),
      }));
    } else if (
      field !== "country_code" &&
      field !== "account_routing_value1" &&
      field !== "account_routing_value2"
    ) {
      setFormData((prevData) => ({
        ...prevData,
        [field]: capitalizeFirstLetter(value),
      }));
    }
  };

  const handlefeildBlur = (field, value) => {
    if (
      field === "postcode" ||
      field === "state" ||
      field === "city" ||
      field === "street_address"
    ) {
      const errorMessage = validateForm3(field, value);
      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: errorMessage,
      }));
    } else if (
      field === "payment_method" ||
      field === "first_name" ||
      field === "last_name" ||
      field === "country_code"
    ) {
      const errorMessage = validateField(field, value);

      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: errorMessage,
      }));
    } else {
      const errorMessage = validateForm2(field, value);
      setErrors((prevErrors) => ({
        ...prevErrors,
        [field]: errorMessage,
      }));
    }
  };

  const validateForm2 = (formField, value) => {
    const fieldDef = filterFields(data).find((f) => f.fieldName === formField);
    const error = validateFieldSecond(fieldDef, value);
    return error;
  };

  const validateFormPage1 = () => {
    const allErrors = {};
    let isValid = true;

    const basicFields = {
      entity_type: formData.entity_type,
      first_name: formData.first_name,
      last_name: formData.last_name,
      country_code: formData.country_code,
      payment_method: formData.payment_method,
    };

    // Check each basic field
    Object.keys(basicFields).forEach((key) => {
      const error = validateField(key, basicFields[key]);
      if (error) {
        allErrors[key] = error;
        isValid = false;
      }
    });

    // Dynamic fields validation if there's a payment method
    if (formData.payment_method && dynamicValidation) {
      dynamicValidation.forEach((key) => {
        if (!key?.fieldName) return;

        const error = validateForm2(key.fieldName, formData[key.fieldName]);
        if (error) {
          allErrors[key.fieldName] = error;
          isValid = false;
        }
      });
    }

    // Update all errors at once
    setErrors(allErrors);
    return isValid;
  };

  const validateFormPage2 = () => {
    const secondPageError = {};
    let isValid = true;
    dynamicAddressValidation?.forEach((key) => {
      const error = validateForm3(key?.fieldName, formData[key?.fieldName]);
      if (error) {
        secondPageError[key?.fieldName] = error;
        isValid = false;
      }
    });

    setErrors(secondPageError);
    return isValid;
  };

  const closeSheet = useCallback(() => {
    if (currentFlow !== "ChangeMethod") {
      setFormData(INITIAL_FORM_DATA);
    }
    if (currentFlow === "addNewPayee") {
      setSelectedMethod(null);
    }
    setFormData(INITIAL_FORM_DATA);
    setChangeMode(false);
    setFromAmountError(false);
    setToAmountError(false);
    setIsOpen(false);
    setStateVariable(1);
    setCurrentFlow("SendMoney");
    setInvestmentAccount("");
    setToAmount(null);
    setAmount(null);
    setData([]);
    setPin(Array(OTP_LENGTH).fill(""));
    setChallengeId("");
    setRemarks("");
    setPurposeOfTransfer("");
    setSelectedMethod(null);
    setCharges({
      totalFees: "-",
      rate: null,
    });
    setFormattedFields([]);
    // queryClient.invalidateQueries({
    //   queryKey: ["transactionDetails"],
    // });
    // queryClient.invalidateQueries({
    //   queryKey: ["uniqueWalletDetails"],
    // });

    setFromAmountConversionError("");
    setConversionError("");
    setRemarkError("");
    setPinError("");
    setHasLimitErrors(false);
  }, [
    currentFlow,
    queryClient,
    setAmount,
    setBeneficiarySelected,
    setCharges,
    setCurrentFlow,
    setData,
    setFormattedFields,
    setFromAmountError,
    setFromAmountConversionError,
    setInvestmentAccount,
    setIsOpen,
    setChallengeId,
    setPin,
    setPinError,
    setRemarkError,
    setPurposeOfTransfer,
    setRemarks,
    setSelectedMethod,
    setStateVariable,
    setToAmount,
    setToAmountError,
    setHasLimitErrors,
  ]);

  const closeSheetRef = useRef(closeSheet);
  closeSheetRef.current = closeSheet;
  const otpLockoutTimer = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      clearTimeout(otpLockoutTimer.current);
      setOtpLockedOut(false);
    }
  }, [isOpen]);

  useEffect(() => {
    return () => clearTimeout(otpLockoutTimer.current);
  }, []);

  // Shows an OTP send/verify error. On a lockout (HTTP 429 or "too many
  // attempts") the footer buttons stay disabled and the drawer closes after
  // OTP_LOCKOUT_CLOSE_MS.
  const handleOtpError = (error, setError, fallback) => {
    const message = error?.message || fallback;
    setError(message);
    const isLockout =
      error?.httpStatus === 429 || /too many/i.test(error?.message || "");
    if (!isLockout) return;
    setOtpLockedOut(true);
    clearTimeout(otpLockoutTimer.current);
    otpLockoutTimer.current = setTimeout(() => {
      setOtpLockedOut(false);
      closeSheetRef.current();
    }, OTP_LOCKOUT_CLOSE_MS);
  };

  const HandleSubmit = async () => {
    if (currentFlow === "SendMoney")
      switch (stateVariable) {
        case 1:
          return setStateVariable(stateVariable + 1);
        case 2:
          if (amount && toAmount && !fromAmountError) {
            setStateVariable(stateVariable + 1);
          } else {
            if (!amount) {
              setFromAmountError("Amount is required");
            }
          }
          break;
        case 3:
          if (remarks && purposeOfTransfer) {
            if (isLoadingTransfer) break;
            setisLoadingTransfer(true);
            try {
              const otpResponse = await SendTransactionOtp({
                channel: API_CHANNEL.WHATSAPP,
              });
              setChallengeId(otpResponse?.data?.challengeId);
              setPin(Array(OTP_LENGTH).fill(""));
              setPinError("");
              setStateVariable(stateVariable + 1);
            } catch (error) {
              handleOtpError(
                error,
                setRemarkError,
                "Failed to send OTP",
              );
            } finally {
              setisLoadingTransfer(false);
            }
          } else if (!purposeOfTransfer) {
            setPurposeError("Purpose of Transfer is required");
          } else {
            setRemarkError("Remark is required");
          }

          break;
        case 4:
          if (pin.some((value) => !value)) {
            setPinError("Please enter the 6 digit OTP.");
            break;
          }
          if (isLoadingTransfer) break;
          setisLoadingTransfer(true);
          try {
            const transfer = await TransferFund({
              beneficiaryId: beneficiarySelected?.beneficiaryId,
              toCurrency:
                selectedMethodToTransfer === "LOCAL"
                  ? beneficiarySelected?.currency
                  : selectedToCurrency?.vendorCurrency,
              fromCurrency: selectedCurrency?.vendorCurrency,
              amount: toAmount,
              transferMethod: selectedMethodToTransfer,
              transactionType: "TRANSFER",
              purpose: investmentAccount
                ? "investment_capital"
                : purposeOfTransfer,
              remarks: remarks,
              challengeId,
              channel: API_CHANNEL.WHATSAPP,
              otp: pin.join(""),
            });

            queryClient.invalidateQueries({
              queryKey: ["transactionDetails"],
            });
            queryClient.invalidateQueries({
              queryKey: ["uniqueWalletDetails"],
            });

            setBenificaryData(transfer);
            setStateVariable(stateVariable + 1);
          } catch (error) {
            handleOtpError(error, setPinError, "Transfer failed");
          } finally {
            setisLoadingTransfer(false);
          }
          break;

        default:
          return setStateVariable(stateVariable + 1);
      }

    if (currentFlow === "addNewPayee")
      switch (stateVariable) {
        case 1:
          if (validateFormPage1() && formData?.payment_method && !loading) {
            setStateVariable(stateVariable + 1);
          }
          break;

        case 2:
          if (validateFormPage2()) {
            try {
              setisLoadingTransfer(true);
              formData.transfer_method = selectedMethod;
              formData.entity_type =
                formData.entity_type === "INDIVIDUAL" ? "PERSONAL" : "COMPANY";
              formData.account_name = formData?.first_name;

              const BenificiaryAdded = await PostBeneficiary(formData);
              setBeneficiaryId(BenificiaryAdded?.data?.beneficiaryId);
            } catch (error) {
              setisLoadingTransfer(false);
              const errorMessage = error?.response?.data?.message || error?.message || "Something went wrong";
              setBeniCreationError(errorMessage);
              console.error("Error:", error?.response?.data || error);
            }
          }

          break;

        default:
          return;
      }
    if (currentFlow === "ChangeMethod") {
      let isValid = true;
      const validationErrors = {};

      // Check required fields
      if (!formData.payment_method) {
        validationErrors.payment_method = "Transfer method is required";
        isValid = false;
      }

      // Validate dynamic fields
      dynamicValidation.forEach((key) => {
        if (!key?.fieldName) return;
        const error = validateForm2(key.fieldName, formData[key.fieldName]);
        if (error) {
          validationErrors[key.fieldName] = error;
          isValid = false;
        }
      });

      // Update errors state
      setErrors(validationErrors);
      if (isValid) {
        setisLoadingTransfer(true);
        formData.transfer_method = selectedMethod;
        formData.account_name = formData?.first_name;
        try {
          const BenificiaryAdded = await PostBeneficiary(formData);
          if (BenificiaryAdded) {
            setBeneficiaryId(BenificiaryAdded?.data?.beneficiaryId);
          }
        } catch (error) {
          setisLoadingTransfer(false);
          setStateVariable(1);
          setCurrentFlow("SendMoney");
          console.log(error);
        }
      } else {
        console.log("Form has validation errors");
      }
    }

    if (currentFlow === "WalletTransfer")
      switch (stateVariable) {
        case 1: {
          if (amount && toAmount && !fromAmountConversionError && !toAmountError) {
            setStateVariable(stateVariable + 1);
          } else {
            if (!amount) {
              setFromAmountConversionError("Amount is required");
            }
          }
          break;
        }
        case 2: {
          if (isLoadingTransfer) break;
          setisLoadingTransfer(true);
          setConversionError("");
          try {
            const otpResponse = await SendTransactionOtp({
              channel: API_CHANNEL.WHATSAPP,
            });
            setChallengeId(otpResponse?.data?.challengeId);
            setPin(Array(OTP_LENGTH).fill(""));
            setPinError("");
            setStateVariable(stateVariable + 1);
          } catch (error) {
            handleOtpError(
              error,
              setConversionError,
              "Failed to send OTP",
            );
          } finally {
            setisLoadingTransfer(false);
          }
          break;
        }
        case 3: {
          if (pin.some((value) => !value)) {
            setPinError("Please enter the 6 digit OTP.");
            break;
          }
          if (isLoadingTransfer || !amount || !toAmount) break;
          setisLoadingTransfer(true);
          try {
            const WalletTransferDetails = await PostWalletTransfer({
              buyAmount: toAmount,
              toCurrency: selectedToCurrency?.vendorCurrency,
              reason: "converting currency",
              fromCurrency: selectedCurrency?.vendorCurrency,
              transactionType: "CONVERSION",
              term_agreement: true,
              challengeId,
              channel: API_CHANNEL.WHATSAPP,
              otp: pin.join(""),
            });
            queryClient.invalidateQueries({
              queryKey: ["transactionDetails"],
            });
            queryClient.invalidateQueries({
              queryKey: ["uniqueWalletDetails"],
            });

            setWalletTransferDetails(WalletTransferDetails);
            setStateVariable(stateVariable + 1);
          } catch (error) {
            handleOtpError(
              error,
              setPinError,
              "Conversion failed",
            );
          } finally {
            setisLoadingTransfer(false);
          }
          break;
        }

        default:
          return setStateVariable(stateVariable + 1);
      }
  };

  const renderContent = () =>
    flows[currentFlow][stateVariable] || (
      <p className="text-center">
        Something when wrong please Refresh or check after sometime
      </p>
    );

  // Define dynamic footer content based on state
  const getFooterContent = () => {
    // Handle cases where no footer should be shown (e.g., SendMoney state 1)
    if (stateVariable === 1 && currentFlow === "SendMoney") {
      return null;
    }

    // Function to render the main footer buttons
    const renderFooterButtons = (cancelHandler, submitHandler, submitText) => (
      <div className="flex ml-auto space-x-2">
        <Button
          variant="outline"
          className="font-geomanistBold rounded-[8px] px-5 py-2.5"
          onClick={cancelHandler}
          disabled={isLoadingTransfer || otpLockedOut}
        >
          Cancel
        </Button>
        <Button
          className="font-geomanistBold rounded-[8px] px-5 py-2.5"
          onClick={submitHandler}
          disabled={isLoadingTransfer || otpLockedOut || isLoading ||
            (currentFlow === "WalletTransfer" &&
              stateVariable === 1 &&
              (!!fromAmountConversionError || !!toAmountError))
          }
        >
          {isLoadingTransfer ? <ButtonLoader className={"mr-2"} /> : submitText}
        </Button>
      </div>
    );

    // Function to render the "Back to Home" button
    const renderBackToHomeButton = (cancelHandler) => (
      <div className="ml-auto">
        <Button
          className="font-geomanistBold rounded-[8px] px-5 py-2.5"
          onClick={cancelHandler}
          size="lg"
        >
          Back to Home
        </Button>
      </div>
    );
    const renderNeedHelpButton = (cancelHandler) => (
      <div className="ml-auto">
        <Button
          className="font-geomanistBold rounded-[8px] px-5 py-2.5"
          // onClick={cancelHandler}
          size="lg"
          onClick={() => {
            window.open(
              "https://borderlesssofttechprivatelimited.zohodesk.in/portal/en/kb/borderless-multicurrency-account",
              "_blank"
            );
            closeSheet();
          }}
        >
          Need help ?
        </Button>
      </div>
    );

    // Use switch statement for different flow cases
    switch (currentFlow) {
      case "SendMoney":
        // Handle "SendMoney" flow
        if (stateVariable !== 5) {
          return renderFooterButtons(
            closeSheet,
            HandleSubmit,
            // "Continue"
            stateVariable === 4 ? "Confirm Transfer" : "Continue"
          );
        } else {
          return renderBackToHomeButton(closeSheet);
        }

      case "addNewPayee": {
        // if (stateVariable !== 3) // Handle "addNewPayee" flow
        return renderFooterButtons(
          closeSheet,
          HandleSubmit,
          // "Continue"
          stateVariable === 2 ? "Add Beneficiary" : "Continue"
        );
      }
      // else {
      //   return renderBackToHomeButton(closeSheet);
      // }
      case "WalletTransfer":
        if (stateVariable !== 4) {
          return renderFooterButtons(
            closeSheet,
            HandleSubmit,
            stateVariable === 3 ? "Confirm Conversion" : "Continue"
          );
        } else {
          return renderBackToHomeButton(closeSheet);
        }
      case "ChangeMethod":
        return renderFooterButtons(
          closeSheet,
          HandleSubmit,
          "Continue"
          // stateVariable === 2 ? "Create Be" : "Continue"
        );

      case "beneficiaryAdded":
        return (
          <div className="flex flex-col w-full gap-3">
            <Button
              className="font-geomanistBold rounded-lg h-12 w-full gap-2 bg-blueish hover:bg-blueish/90 text-white"
              onClick={() => {
                window.open(
                  buildSupportEmailUrl(addedBeneficiary),
                  "_blank",
                  "noopener,noreferrer"
                );
              }}
            >
              <Mail size={18} />
              Email support
            </Button>
            <Button
              variant="outline"
              className="font-geomanistBold rounded-lg h-12 w-full border-blueish !text-blueish"
              onClick={closeSheet}
            >
              Done
            </Button>
          </div>
        );

      case "AddFunds":
        return renderNeedHelpButton(closeSheet);

      case "beniList":
        return renderNeedHelpButton(closeSheet);

      case "ManageCard":
        if (stateVariable === 1) {
          // If card is frozen, only show Cancel button
          if (isCardFrozen) {
            return (
              <div className="ml-auto">
                <Button
                  variant="outline"
                  className="font-geomanistBold rounded-[8px] px-5 py-2.5"
                  onClick={closeSheet}
                  disabled={isLoadingTransfer}
                >
                  Cancel
                </Button>
              </div>
            );
          }

          // If card is active, show both Cancel and Save buttons
          const isSaveDisabled = isLoadingTransfer || isLoading || hasLimitErrors || isSavingLimits;
          return (
            <div className="flex ml-auto space-x-2">
              <Button
                variant="outline"
                className="font-geomanistBold rounded-[8px] px-5 py-2.5"
                onClick={closeSheet}
                disabled={isLoadingTransfer || isSavingLimits}
              >
                Cancel
              </Button>
              <Button
                className={`font-geomanistBold rounded-[8px] px-5 py-2.5 ${isSaveDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
                onClick={async () => {
                  // Call the save handler exposed by ManageCard component
                  if (window.saveTransactionLimits && !isSaveDisabled) {
                    await window.saveTransactionLimits();
                  }
                }}
                disabled={isSaveDisabled}
              >
                {(isLoadingTransfer || isSavingLimits) ? <ButtonLoader className={"mr-2"} /> : "Save"}
              </Button>
            </div>
          );
        }
        return null;

      default:
        // Default case for unknown flows
        return renderBackToHomeButton(closeSheet);
    }
  };

  const flows = {
    SendMoney: {
      1: (
        <SendMoney
          setStateVariable={setStateVariable}
          stateVariable={stateVariable}
          setBeneficiarySelected={setBeneficiarySelected}
          setCurrentFlow={setCurrentFlow}
        />
      ),
      2: (
        <TransferDetails
          changeMode={changeMode}
          setChangeMode={setChangeMode}
          beneficiarySelected={beneficiarySelected}
        />
      ),
      3: <PaymentSummary beneficiarySelected={beneficiarySelected} />,
      4: <OtpConfirmation />,
      5: <PaymentConfirmation />,
    },
    addNewPayee: {
      1: (
        <AddBeneficiary
          HandleSubmit={HandleSubmit}
          formData={formData}
          errors={errors}
          handleInputChange={handleInputChange}
          handlefeildBlur={handlefeildBlur}
          setFormData={setFormData}
          setErrors={setErrors}
          setDynamicValidation={setDynamicValidation}
          setDynamicAddressValidation={setDynamicAddressValidation}
        />
      ),

      2: (
        <AddBeneficiary2
          formData={formData}
          errors={errors}
          HandleSubmit={HandleSubmit}
          handleInputChange={handleInputChange}
        />
      ),
    },
    WalletTransfer: {
      1: <WalletTransfer />,
      2: (
        <>
          <WalletSummary />
          {conversionError && (
            <p className="text-red-500 text-sm text-center mt-2">
              {conversionError}
            </p>
          )}
        </>
      ),
      3: <OtpConfirmation purpose="conversion" />,
      4: (
        <WalletConfirmation
          walletTransferDetails={walletTransferDetails?.data}
        />
      ),
    },
    AddFunds: {
      1: <AddFundsDrawer />,
    },
    beniList: {
      1: <BenificaryList />,
    },
    beneficiaryAdded: {
      1: <BeneficiaryAdded beneficiary={addedBeneficiary} />,
    },
    ChangeMethod: {
      1: (
        <ChangeMethod
          HandleSubmit={HandleSubmit}
          formData={formData}
          handlefeildBlur={handlefeildBlur}
          setFormData={setFormData}
          errors={errors}
          handleInputChange={handleInputChange}
          setDynamicValidation={setDynamicValidation}
          setErrors={setErrors}
          setDynamicAddressValidation={setDynamicAddressValidation}
        />
      ),
    },
    ManageCard: {
      1: (
        <ManageCard
          queryClient={queryClient}
          onCloseDrawer={closeSheet}
          setIsCardFrozen={setIsCardFrozen}
          setHasLimitErrors={setHasLimitErrors}
          setIsSavingLimits={setIsSavingLimits}
          onSave={async () => {
            if (window.saveTransactionLimits) {
              return await window.saveTransactionLimits();
            }
            return false;
          }}
        />
      ),
    },
  };

  const handleDecrement = () => {
    if (currentFlow === "ChangeMethod" && stateVariable === 1) {
      setStateVariable(2);
      setCurrentFlow("SendMoney");
      return;
    }

    if (currentFlow === "addNewPayee" && stateVariable === 1) {
      setCurrentFlow("SendMoney");
      setStateVariable(1);
      setSelectedMethod(null);
      setFormattedFields([]);
      setFormData({
        entity_type: "",
        first_name: "",
        last_name: "",
        country_code: "",
        payment_method: "",
        postcode: "",
        state: "",
        city: "",
        street_address: "",
        transfer_method: "",
      });
      return;
    } else if (currentFlow === "addNewPayee" && stateVariable === 2) {
      setStateVariable((prevState) => prevState - 1);
      return;
    }

    if (currentFlow === "SendMoney" && stateVariable === 2) {
      setInvestmentAccount("");
      setToAmount(null);
      setBeneficiarySelected({});
      setPurposeOfTransfer("");
      setRemarks("");
      setAmount(null);
      setCharges({
        totalFees: "-",
        rate: null,
      });
    }

    setChangeMode(false);
    setIsHedgeFundAccount("");
    setStateVariable((prevState) => prevState - 1);
    setData([]);
    setSelectedMethod(null);
    setPin(Array(OTP_LENGTH).fill(""));
    setChallengeId("");
    setFromAmountConversionError("");
    setFormattedFields([]);
    setFormData({
      entity_type: "",
      first_name: "",
      last_name: "",
      country_code: "",
      payment_method: "",
      postcode: "",
      state: "",
      city: "",
      street_address: "",
      transfer_method: "",
    });
  };

  return (
    <>
      {isOpen && (
        <DrawerContainer
          isLoading={isLoadingTransfer || isLoading}
          stateVariable={stateVariable}
          closeSheet={closeSheet}
          setStateVariable={setStateVariable}
          title={getTitle()}
          footerContent={getFooterContent()}
          handleDecrement={handleDecrement}
          currentFlow={currentFlow}
          isLoadingTransfer={isLoadingTransfer}
        >
          {renderContent()}
        </DrawerContainer>
      )}
    </>
  );
};

export default CustomSheet;
